import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format, isToday, isTomorrow } from "date-fns";
import { useFragment } from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Avatar } from "@whoosh/design-system/src/components/display";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";
import { convertServerTimeToLocal } from "@whoosh/utilities/src/dates";

import { useCurrentSignUp } from "../../useCurrentSignUp/useCurrentSignUp";
import { EventDetailYourSignup_event$key } from "./__generated__/EventDetailYourSignup_event.graphql";

type EventDetailYourSignupProps = {
  fragmentRef: EventDetailYourSignup_event$key;
};

const formatSmartDeadline = (dateString: string): string => {
  const date = convertServerTimeToLocal(dateString);
  const timeFormatted = format(date, "h:mm aa");

  if (isToday(date)) {
    return t`today at ${timeFormatted}`;
  }
  if (isTomorrow(date)) {
    return t`tomorrow at ${timeFormatted}`;
  }
  return format(date, "MMM do") + ` at ${timeFormatted}`;
};

export const EventDetailYourSignup = ({
  fragmentRef,
}: EventDetailYourSignupProps) => {
  const event = useFragment(EventDetailYourSignupFragmentString, fragmentRef);
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const signUp = useCurrentSignUp(event);
  const eventPlayerNoun = getNounOverride(
    event.eventSeries?.facility?.copyOverrides?.eventPlayer?.singular,
    t`Player`
  );

  const cancelDeadlineAt = signUp?.signUpWindow?.cancelDeadlineAt ?? null;
  const canCancelSignUp = signUp?.canDelete === true;

  const registrations = signUp?.registrations?.edges;

  const handleManageSignup = () => {
    if (!signUp?.id) return;
    navigation.navigate("EventSignupSummary", {
      eventID: event.id,
      signUpId: signUp.id,
    });
  };

  return (
    <VStack
      borderWidth={4}
      borderColor="grayscale-200"
      borderRadius="4"
      padding={6}
      gap={4}
    >
      <Text variant="mainHeading">{t`Your Signup`}</Text>

      <VStack gap={1}>
        {registrations?.map((edge) => {
          const registration = edge?.node;
          if (!registration) return null;

          const name =
            registration.registrant?.firstName &&
            registration.registrant?.lastName
              ? `${registration.registrant.firstName} ${registration.registrant.lastName}`
              : registration.unknownRegistrantDisplayName ??
                (registration.type === "CUSTOMER"
                  ? t`New Player`
                  : t`Unknown ${eventPlayerNoun}`);

          const avatarType =
            registration.type === "MEMBER"
              ? "member"
              : registration.type === "GUEST"
              ? "guest"
              : "customer";

          return (
            <HStack
              key={registration.id}
              alignItems="center"
              justifyContent="space-between"
              gap={3}
            >
              <HStack flex={1} alignItems="center" gap={3}>
                <Avatar
                  size="xs"
                  profileImagePublicId={
                    registration.registrant?.profileImage?.publicId
                  }
                  firstName={registration.registrant?.firstName}
                  lastName={registration.registrant?.lastName}
                  type={avatarType}
                />
                <Text variant="mainBaseSemiBold">{name}</Text>
              </HStack>

              {/* TODO: Cleanup in future event prepayment tickets */}
              {/* <Box
                bg="surface-status-success-subtlest"
                borderRadius="1"
                height={18}
                alignItems="center"
                justifyContent="center"
                px={1}
              >
                <Text variant="mainMicro" color="text-primary">
                  {t`PAID`}
                </Text>
              </Box> */}
            </HStack>
          );
        })}
      </VStack>

      <VStack>
        <Button
          variant="secondary"
          width="100%"
          onPress={handleManageSignup}
          accessibilityLabel={t`Manage Signup`}
        >
          {t`Manage Signup`}
        </Button>

        {canCancelSignUp && cancelDeadlineAt ? (
          <HStack justifyContent="center" alignItems="center" py={2}>
            <Box
              bg="surface-secondary"
              px={4}
              py={2}
              style={{ borderRadius: 100 }}
            >
              <Text variant="mainSmallSemiBold">
                {t`Cancel available until ${formatSmartDeadline(
                  cancelDeadlineAt
                )}`}
              </Text>
            </Box>
          </HStack>
        ) : null}
      </VStack>
    </VStack>
  );
};

const EventDetailYourSignupFragmentString = graphql`
  fragment EventDetailYourSignup_event on Event {
    ...useCurrentSignUp_event
    id
    eventSeries {
      facility {
        copyOverrides {
          eventPlayer {
            singular
          }
        }
      }
    }
  }
`;
